import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

// Light or dark appearance. 'auto' follows the OS setting; an explicit choice
// is remembered in localStorage. The inline script in index.html applies the
// same logic before first paint (no flash of the wrong theme) — keep the two in
// step. This provider keeps it applied as the choice or the OS setting changes.
export type ThemePref = 'auto' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

export const THEME_PREFS: ThemePref[] = ['auto', 'light', 'dark']
const KEY = 'pa_theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

// `read` may throw (private mode, blocked storage).
export function readPref(read: () => string | null): ThemePref {
  try {
    const v = read()
    return v === 'light' || v === 'dark' ? v : 'auto'
  } catch {
    return 'auto'
  }
}

export function resolveTheme(pref: ThemePref, systemDark: boolean): Theme {
  return pref === 'auto' ? (systemDark ? 'dark' : 'light') : pref
}

// The browser chrome / iOS status bar colour: each theme's --bg.
export function themeColor(theme: Theme): string {
  return theme === 'dark' ? '#000' : '#fafaf7'
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor(theme))
}

const ThemeContext = createContext<{ pref: ThemePref; setPref: (p: ThemePref) => void }>({
  pref: 'auto',
  setPref: () => {},
})

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPref] = useState<ThemePref>(() => readPref(() => localStorage.getItem(KEY)))
  useEffect(() => {
    try {
      if (pref === 'auto') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, pref)
    } catch {
      // Private mode or blocked storage: the choice just isn't remembered.
    }
    const mql = window.matchMedia(DARK_QUERY)
    const update = () => apply(resolveTheme(pref, mql.matches))
    update()
    if (pref !== 'auto') return
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [pref])
  return <ThemeContext.Provider value={{ pref, setPref }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  return useContext(ThemeContext)
}
